import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { isEmpty } from 'ramda'
import { useMakeSelector, useSelector } from 'hooks/redux'
import strings from 'utils/strings'

import {
  getPtoTemplateDiff,
  makeGetTemplatesSelector,
  selectPtoTemplateFragments,
  selectPtoVehicleFragments,
} from 'selectors/powerTakeOff'

import {
  NotesTitle,
  TemplateName,
  StyledTextArea,
  StyledTable,
  TableSubTitle,
} from './PtoTemplateNotes.style'

const columns = [
  {
    title: strings.name_title,
    dataIndex: 'name',
    key: 'name',
    width: '42.39%',
  },
  {
    title: strings.value,
    dataIndex: 'templateValue',
    key: 'templateValue',
    width: '15.20%',
  },
]

const skippedFragmentColumns = [
  {
    title: strings.domain,
    dataIndex: 'domainId',
    key: 'domainId',
    width: '42.39%',
  },
  ...columns,
]

const updatedDataColumns = [
  {
    title: strings.domain,
    dataIndex: 'domainName',
    key: 'domainName',
    width: '42.39%',
  },
  ...columns,
]

const getSkippedFragments = (vehicleFragments, templateFragments) =>
  vehicleFragments.filter((fragment) => {
    const templateFragment =
      templateFragments.find(({ refNo }) => fragment.refNo === refNo) || {}
    if (templateFragment && templateFragment.valueRaw === fragment.valueRaw) {
      return true
    }

    return false
  })

const PtoTemplateNotes = ({ selectedTemplateId, handleNotesChange }) => {
  const [notesVal, setNotesVal] = useState('')
  const diffListByTemplateId = useSelector(getPtoTemplateDiff, {
    templateId: selectedTemplateId,
  })
  const templateFragmentsList = useSelector(selectPtoTemplateFragments, {
    templateId: selectedTemplateId,
  })
  const ptoVehicleFragmentsList = useSelector(selectPtoVehicleFragments)

  const templates = useMakeSelector(makeGetTemplatesSelector)

  const selectedTemplate = templates[selectedTemplateId]
  const { name: templateName } = selectedTemplate

  const onNotesChange = (e) => {
    setNotesVal(e.target.value)
    handleNotesChange(e.target.value)
  }

  const renderFragmentListForSave = useMemo(
    () =>
      !isEmpty(diffListByTemplateId) && (
        <StyledTable
          columns={updatedDataColumns}
          dataSource={diffListByTemplateId}
          pagination={false}
          yScroll={350}
          rowKey={(record) => record.refNo}
        />
      ),
    [diffListByTemplateId]
  )

  const skippedFragments = useMemo(() => {
    const data = getSkippedFragments(
      ptoVehicleFragmentsList,
      templateFragmentsList
    )

    return (
      <StyledTable
        columns={skippedFragmentColumns}
        dataSource={data}
        pagination={false}
        yScroll={350}
        rowKey={(record) => record.refNo}
      />
    )
  }, [ptoVehicleFragmentsList, templateFragmentsList])

  return (
    <>
      <NotesTitle>
        <TemplateName size="large" weight="bold">
          {strings.apply_template(templateName)}
        </TemplateName>
      </NotesTitle>

      <TableSubTitle>{strings.pto_template_skipped_fragments}</TableSubTitle>
      {skippedFragments}

      <TableSubTitle>{strings.pto_template_updated_fragments}</TableSubTitle>
      {renderFragmentListForSave}

      <StyledTextArea
        size="large"
        onChange={onNotesChange}
        value={notesVal}
        placeholder={strings.notes_required}
        autosize={{ minRows: 8, maxRows: 15 }}
        maxLength={1000}
      />
    </>
  )
}

PtoTemplateNotes.propTypes = {
  selectedTemplateId: PropTypes.string,
  handleNotesChange: PropTypes.func,
  notes: PropTypes.string,
}

export default PtoTemplateNotes
